import api from './api';
import store from '@/store';
import TokenService from './token.service';
import { localStorageName } from '@/config/utils';

class MemberServie {
  getDataMember(dataForm) {
    return api.post('/get-data-member', dataForm);
  }

  getTopupList() {
    return api.get(`${process.env.VUE_APP_BASE_MINTY_URL}/member/topup-list?get_all=true`);
  }

  // Same endpoint the member site's MyTransactions screen uses — the shared
  // axios instance already attaches the member Bearer token + MerchantCode,
  // so nothing extra is needed for the jwt.member guard behind it.
  getMyTransactions(type, timeSelect, paramsFilter = {}) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/my-transactions`, {
      type: type,
      timeSelect: timeSelect,
      ...paramsFilter
    });
  }

  // Wallet balance for the paid-membership screen. NOT get-data-member-moo:
  // that returns amount_in_account (cashback + top-up), while the wallet
  // payment below can only draw on getTotalTopupAmountCanSpent — checking the
  // wrong one lets a member proceed and fail server-side.
  getProfile() {
    return api.get(`${process.env.VUE_APP_BASE_MINTY_URL}/member/get-profile`);
  }

  getDefaultPaymentMode() {
    return api.get(`${process.env.VUE_APP_BASE_MINTY_URL}/member/get-default-payment-mode`);
  }

  activatePaidMembershipWithWallet(dataForm) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/assign-paid-membership-rewards-to-member-using-cashback-topup`, dataForm);
  }

  // The MEMBER API's payment endpoints, not OrderService's: the MOO ordering
  // API only understands isPaymentItem / isTopupOnMember and rejects anything
  // else. What the payment is FOR is decided by the flags in the payload —
  // isPaidMembership + tier_id, or isExchangeVoucherBundle + voucher_id —
  // which the BE copies into the session metadata for the webhook to dispatch.
  checkoutStripe(dataForm) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/payment/checkout-session-stripe`, dataForm);
  }

  checkoutAirwallex(dataForm) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/payment/checkout-hpp-airwallex`, dataForm);
  }

  // Confirms a card payment on return and, if the gateway webhook has not
  // landed yet, re-dispatches it — so what was paid for is granted even when
  // the webhook was missed. Same pair for every flow.
  checkPaymentStripe(dataForm) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/payment/check-checkout-session-stripe`, dataForm);
  }

  checkPaymentAirwallex(dataForm) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/payment/check-hpp-airwallex`, dataForm);
  }

  // Verifying a card payment only QUEUES the job that actually grants what was
  // paid for, so a single check call cannot tell "paid and granted" from "paid,
  // still queued" or "paid, then the job failed". The check endpoints answer
  // that with a three-state contract: an error response means the job ran and
  // failed, data.job_status 'pending' means still queued, anything else means
  // done. Poll until it settles so no screen reports success on a job that has
  // not run — every card flow (top-up, voucher, bundle, paid membership) lands
  // on the same pair of endpoints and needs the same wait.
  //
  // Returns { status: 'success' | 'pending' | 'failed', message? }. 'pending'
  // means the window ran out, NOT that anything went wrong: the payment is
  // taken and the job is still coming, so callers say so rather than promising
  // a result they cannot stand behind.
  async pollPaymentJobStatus(stripeId, airwallexIntentId) {
    const MAX_ATTEMPTS = 8;
    const INTERVAL_MS = 1500;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, INTERVAL_MS));
      }

      let response;
      try {
        response = await (stripeId
          ? this.checkPaymentStripe({ stripe_id: stripeId })
          : this.checkPaymentAirwallex({ payment_intent_id: airwallexIntentId }));
      } catch (error) {
        return {
          status: 'failed',
          message: error?.response?.data?.result?.message ?? 'Payment check failed',
        };
      }

      if (!response.data.result.isSuccess) {
        return { status: 'failed', message: response.data.result.message };
      }

      // Endpoints that queue nothing return no job_status at all; treat that as
      // done rather than polling something that will never change.
      if (response.data.data?.job_status !== 'pending') {
        return { status: 'success' };
      }
    }

    return { status: 'pending' };
  }

  // Branch directory with opening hours and address, same endpoint the member
  // site's FindStores screen uses. Behind jwt.merchant_id_and_code only — no
  // member login needed, just the headers the interceptor always sends.
  findBranches(search_keyword) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/find-branch`, {
      search_keyword: search_keyword,
    });
  }

  // Same endpoint the member site's LoyaltyTierBenefit screen uses.
  getListTier() {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/tier-info`);
  }

  getBranches() {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/branches`);
  }

  addDirectAmountAfterPayWithCard(session_id, amount_direct_increased, id, merchant_code) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/add-direct-amount-after-pay-with-card`, { session_id: session_id, amount_direct_increased: amount_direct_increased, topup_id: id }, {
        headers: {
        'MerchantCode': merchant_code
        }
      }
    );
  }

  checkMemberPermissions() {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/check-member-permissions`, {
      merchant_code: store.state.merchantCode
    });
  }

  getCountryDropdown() {
    return api.get(`${process.env.VUE_APP_BASE_MINTY_URL}/countries-for-dropdown`);
  }

  // Full country list (id + country_name) for the "Country Of Resident" field.
  // countries-for-dropdown above is the PHONE-CODE list, a different shape.
  getCountries() {
    return api.get(`${process.env.VUE_APP_BASE_MINTY_URL}/countries`);
  }

  // The write half of getProfile() above — the member site's own profile pair,
  // not get-data-member-moo / update-data-member-moo. Those accept a fixed six
  // fields and expose nothing about the merchant's field configuration, so MOO
  // could neither render nor collect the fields a merchant actually asked for,
  // and their update path grants the profile-sharing voucher without validating
  // anything. update-profile validates through MemberProfileRequest and stores
  // the merchant's custom fields.
  //
  // multipart because a custom field can be a file upload: axios serialises the
  // payload to FormData itself when the Content-Type says so, which is how the
  // File objects inside fields_another survive the trip.
  updateMemberProfile(dataForm) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/update-profile`, dataForm, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  getMerchantId(dataForm) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/pos/get-merchant-id`, dataForm);
  }

  getMemberInfo(merchant_code) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/get-data-member-moo`, {}, {
      headers: {
        'MerchantCode': merchant_code
      }
    });
  }

  validatePhone(data, merchant_code) {
    return api.post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/validate-phone`, data, {
      headers: {
        'MerchantCode': merchant_code
      }
    });
  }

  loginPhoneNumber({ phone_number, phone_code }, merchant_code) {
    return api
      .post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/login-phone-number`, {
        phone_number,
        phone_code
      }, {
        headers: {
          'MerchantCode': merchant_code
        }
      })
      .then((response) => {
        if (response.data.result.isSuccess) {
          TokenService.setMember(response.data);
          localStorage.setItem(localStorageName('KIOSK_MEMBER_INFO'), JSON.stringify(response.data.data.user));
          localStorage.setItem(localStorageName('token'), response.data.data.authorisation.token);
        }
        return response.data;
      });
  }

  // KSK095: the member is cleared locally right away, whatever /member/logout
  // answers — an expired token makes it fail (500), and the kiosk then stayed
  // signed in forever. The token is read first so the request still carries it.
  logout(merchant_code) {
    const token = localStorage.getItem(localStorageName('token'));
    const headers = { 'MerchantCode': merchant_code };
    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }
    const request = api
      .post(`${process.env.VUE_APP_BASE_MINTY_URL}/member/logout`, {}, { headers })
      .then((response) => response.data);

    TokenService.removeUser();
    localStorage.removeItem(localStorageName('KIOSK_MEMBER_INFO'));
    localStorage.removeItem(localStorageName('token'));

    return request;
  }
}

export default new MemberServie();
